// Map a parsed DocAST → IngestPlan. Replaces the regex-per-field
// extractor. Same external shape (IngestPlan) so the UI doesn't change.
// Provenance: every resolved field carries its sentence index + span,
// so the wizard can highlight where each value came from.

import { parseBriefAST, entitiesOfKind, type Entity, type DocAST } from "./ast";
import { REQUIRED_FIELDS, type RequiredField } from "./required";
import type {
  ClarificationQuestion,
  IngestField,
  IngestPlan,
  IngestSource,
  ConfidenceBand,
} from "./types";

function uuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function bandFor(c: number): ConfidenceBand {
  if (c >= 0.85) return "high";
  if (c >= 0.6)  return "medium";
  if (c >= 0.3)  return "low";
  return "missing";
}

interface FieldResolution {
  value?: string;
  raw?: string;
  confidence: number;
}

function pickHighest(entities: Entity[]): Entity | null {
  if (entities.length === 0) return null;
  return entities.reduce<Entity>((best, e) => (e.confidence > best.confidence ? e : best), entities[0]!);
}

function resolveField(field: RequiredField, ast: DocAST): FieldResolution {
  switch (field.label) {
    case "Project kind": {
      const e = pickHighest(entitiesOfKind(ast, "PropertyKind"));
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Project city": {
      const e = pickHighest(entitiesOfKind(ast, "Address"));
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Carpet area (sqft)": {
      const qty = entitiesOfKind(ast, "Quantity").filter((q) =>
        q.unit === "sqft" || q.unit === "squarefeet",
      );
      const e = pickHighest(qty);
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Number of bedrooms": {
      const counts = entitiesOfKind(ast, "Count").filter((c) =>
        c.unit === "bhk" || c.unit?.startsWith("bedroom") || c.unit === "br",
      );
      const e = pickHighest(counts);
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Budget tier": {
      const tier = pickHighest(entitiesOfKind(ast, "Tier"));
      if (tier) return { value: tier.value, raw: tier.raw, confidence: tier.confidence };
      const money = pickHighest(entitiesOfKind(ast, "Money"));
      if (money && money.magnitude !== undefined) {
        const inr = money.magnitude;
        const tierVal = inr >= 3e7 ? "luxury" : inr >= 1e7 ? "premium" : "standard";
        return { value: tierVal, raw: money.raw, confidence: 0.7 };
      }
      return { confidence: 0 };
    }
    case "Design style": {
      const e = pickHighest(entitiesOfKind(ast, "Style"));
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Move-in date": {
      const e = pickHighest(entitiesOfKind(ast, "Date"));
      if (!e) return { confidence: 0 };
      return { value: e.value, raw: e.raw, confidence: e.confidence };
    }
    case "Primary occupants": {
      const occupants = entitiesOfKind(ast, "OccupantClass");
      if (occupants.length === 0) return { confidence: 0 };
      const uniq = Array.from(new Set(occupants.map((o) => o.value)));
      const avg = occupants.reduce((s, o) => s + o.confidence, 0) / occupants.length;
      return { value: uniq.join(", "), raw: occupants.map((o) => o.raw).join(", "), confidence: avg };
    }
    default:
      return { confidence: 0 };
  }
}

function clarificationFor(field: RequiredField): ClarificationQuestion {
  return {
    question_id: uuid(),
    for_field_label: field.label,
    prompt: `Could you tell us the ${field.label.toLowerCase()}?`,
    format: field.format,
    ...(field.choices ? { choices: field.choices } : {}),
    why: field.why,
  };
}

export function extractFromBrief(brief: string, fileName = "brief.txt"): IngestPlan {
  const source: IngestSource = {
    source_id: uuid(),
    kind: "brief",
    name: fileName,
    bytes: brief.length,
    received_at: new Date().toISOString(),
  };

  const ast = parseBriefAST(brief);
  const resolved: IngestField[] = [];
  const clarifications: ClarificationQuestion[] = [];

  for (const f of REQUIRED_FIELDS) {
    const r = resolveField(f, ast);
    const band = bandFor(r.confidence);
    const base: IngestField = {
      label: f.label,
      ontology_iri: f.ontology_iri,
      ...(r.raw !== undefined ? { raw_snippet: r.raw } : {}),
      ...(r.value !== undefined ? { value: r.value } : {}),
      confidence: r.confidence,
      band,
    };
    resolved.push(base);
    if (band === "low" || band === "missing") clarifications.push(clarificationFor(f));
  }

  const total = REQUIRED_FIELDS.length;
  const high = resolved.filter((f) => f.band === "high" || f.band === "medium").length;
  const coverage = total === 0 ? 1 : high / total;

  return {
    plan_id: uuid(),
    sources: [source],
    resolved,
    clarifications,
    coverage,
    ready_to_ingest: clarifications.length === 0,
  };
}

export const SAMPLE_BRIEF = [
  "Anika & Rohan, a young couple with one toddler and a labrador,",
  "are moving into a 1,450 sqft 3BHK apartment in Bengaluru in October 2026.",
  "Budget around ₹1.6 Cr. They want a modern, family-friendly home with",
  "a small home office corner. Move-in is non-negotiable.",
].join(" ");
